import { useDispatch, useSelector } from "react-redux";
import { useEffect, useState, useCallback, useRef } from "react";
import { App } from "antd";
import { COURSES, LOADING_COURSES, COURSES_PAGINATION } from "../store/selectors";
import {
  setLoading,
  setCoursesWithPagination,
  deleteCourse as deleteCourseAction,
} from "../store/slices/course";
import {
  getCourses,
  getAllCourses,
  deleteCourse as deleteCourseService,
  getCourseById as getCourseByIdService,
} from "../services/courses/courses.service";
import { SearchCourseParams } from "../services/courses/courses.types";
import { CourseMinimalResponse } from "../types/api/getCourse";

export const useCourses = () => {
  // From App.useApp(): the static notification does not render under React 19.
  const { notification } = App.useApp();
  const dispatch = useDispatch();
  const courses = useSelector(COURSES);
  const loading = useSelector(LOADING_COURSES);
  const pagination = useSelector(COURSES_PAGINATION);

  const [searchParams, setSearchParams] = useState<SearchCourseParams>({
    page: 1,
    limit: 10,
  });

  const searchParamsRef = useRef(searchParams);
  searchParamsRef.current = searchParams;

  const fetchCourses = useCallback(
    async (params?: SearchCourseParams) => {
      dispatch(setLoading(true));
      try {
        const coursesResponse = await getCourses(params || searchParamsRef.current);
        dispatch(setCoursesWithPagination(coursesResponse));
      } catch (error: any) {
        dispatch(setLoading(false));
        notification["error"]({
          message: "Error",
          description:
            "La información no pudo ser cargada, por favor notifica éste error!",
          placement: "top",
          duration: 3,
        });
        console.error("Error fetching courses:", error);
      }
    },
    [dispatch, notification]
  );

  const updateSearchParams = useCallback(
    async (newParams: Partial<SearchCourseParams>) => {
      const currentParams = searchParamsRef.current;
      const updatedParams = { ...currentParams, ...newParams };
      if (
        newParams.title !== undefined ||
        newParams.subject_id !== undefined ||
        newParams.program_code !== undefined ||
        newParams.id !== undefined
      ) {
        updatedParams.page = 1;
      }
      setSearchParams(updatedParams);
      try {
        await fetchCourses(updatedParams);
      } catch (error) {
        console.error("Error updating search params:", error);
      }
    },
    [fetchCourses]
  );

  const changePage = useCallback(
    async (page: number, pageSize?: number) => {
      const currentParams = searchParamsRef.current;
      const newParams = { ...currentParams, page };
      if (pageSize) {
        newParams.limit = pageSize;
      }
      setSearchParams(newParams);
      try {
        await fetchCourses(newParams);
      } catch (error) {
        console.error("Error changing page:", error);
      }
    },
    [fetchCourses]
  );

  const getCourseById = async (id: number) => {
    try {
      const response = await getCourseByIdService(id);
      return response;
    } catch (error: any) {
      notification["error"]({
        message: "Error",
        description:
          "La información no pudo ser cargada, por favor notifica éste error!",
        placement: "top",
        duration: 3,
      });
      console.error("Error getting course by ID:", error);
      throw new Error(error);
    }
  };

  const deleteCourse = async (id: number): Promise<boolean> => {
    dispatch(setLoading(true));
    try {
      await deleteCourseService(id);
      dispatch(deleteCourseAction(id));
      notification["success"]({
        message: "Éxito",
        description: "El curso fue eliminado exitosamente!",
        placement: "top",
        duration: 3,
      });
      dispatch(setLoading(false));
      try {
        await fetchCourses(searchParamsRef.current);
      } catch (error) {
        console.error("Error refetching courses after delete:", error);
      }
      return true;
    } catch (error: any) {
      dispatch(setLoading(false));
      console.error("Error deleting course:", error);
      notification["error"]({
        message: "Eliminación Erronea",
        description:
          error?.response?.data?.message || "El curso no pudo ser eliminado!",
        placement: "top",
        duration: 3,
      });
      return false;
    }
  };

  const getAllCoursesData = async (): Promise<CourseMinimalResponse[]> => {
    try {
      const response = await getAllCourses();
      return response || [];
    } catch (error: any) {
      notification["error"]({
        message: "Error",
        description:
          "No se pudieron cargar los cursos, por favor notifica este error!",
        placement: "top",
        duration: 3,
      });
      console.error("Error getting all courses:", error);
      return [];
    }
  };

  useEffect(() => {
    fetchCourses().catch((error) => {
      console.error("Error in initial fetch:", error);
    });
  }, [fetchCourses]);

  return {
    loading,
    courses,
    pagination,
    searchParams,
    deleteCourse,
    getCourseById,
    fetchCourses,
    updateSearchParams,
    changePage,
    getAllCoursesData,
  };
};
